import {
    cloneElement,
    useState,
    type FC
} from "react";
import {
    type AccessibilityActionEvent,
    Pressable,
    Vibration,
    Platform,
    View
} from "react-native";
import type IContextMenuTriggerProps from "./type";
import type {
    ContextMenuTriggerWebPropsType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    getInteractionStateName,
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useDropdownMenuContext
} from "../../../dropdownMenu/context";
import type {
    DropdownMenuKeyboardEventType
} from "../../../dropdownMenu/type";
import type {
    ContextMenuTriggerElementType,
    ContextMenuMouseEventType
} from "../../type";
import {
    DROPDOWN_MENU_CONTEXT_MENU_KEY,
    getDropdownMenuSelectionText,
    getIsDropdownMenuEditable,
    getDropdownMenuElement
} from "../../../dropdownMenu/util";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import StateLayer from "../../../stateLayer";

const CONTEXT_MENU_ACCESSIBILITY_ACTION = "longpress";

const ContextMenuTrigger: FC<IContextMenuTriggerProps> = ({
    customTheme: customThemeProp,
    children,
    style
}) => {
    const context = useDropdownMenuContext({
        part: "Trigger"
    });

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        shapeConfigs,
        durations,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    const [
        isPressed,
        setIsPressed
    ] = useState(false);

    const [
        isHovered,
        setIsHovered
    ] = useState(false);

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        isDisabled: context?.isDisabled
    });

    if(!context) {
        return null;
    }

    const {
        handlePointerChange,
        handleOpenChange,
        pointerAnchorRef,
        isDisabled,
        anchorRef,
        contentId,
        isOpen,
        pointer
    } = context;

    const childElement: ContextMenuTriggerElementType = children;

    const isWeb = Platform.OS === "web";

    const getAnchorRect = () => getDropdownMenuElement(anchorRef.current)?.getBoundingClientRect();

    const onContextMenu = (event: ContextMenuMouseEventType) => {
        if(isDisabled || getIsDropdownMenuEditable(event.target) || getDropdownMenuSelectionText()) {
            return;
        }

        const rect = getAnchorRect();

        event.preventDefault();

        handlePointerChange({
            start: rect ? (direction === "rtl" ? rect.right - event.clientX : event.clientX - rect.left) : 0,
            top: rect ? event.clientY - rect.top : 0
        });

        handleOpenChange(true, "pointer");
    };

    const onKeyDown = (event: DropdownMenuKeyboardEventType) => {
        const isMenuKey = event.key === DROPDOWN_MENU_CONTEXT_MENU_KEY || (event.shiftKey && event.key === "F10");

        if(!isMenuKey || isDisabled || getIsDropdownMenuEditable(event.target)) {
            return;
        }

        event.preventDefault();

        handlePointerChange({
            top: getAnchorRect()?.height ?? 0,
            start: 0
        });

        handleOpenChange(true, "first");
    };

    const handleLongPress = () => {
        if(isDisabled) {
            return;
        }

        if(Platform.OS === "android") {
            Vibration.vibrate(durations.haptic);
        }

        handlePointerChange(null);

        handleOpenChange(true, "pointer");
    };

    const handlePressIn = () => {
        setIsPressed(true);
    };

    const handlePressOut = () => {
        setIsPressed(false);
    };

    const handlePointerEnter = () => {
        setIsHovered(true);
    };

    const handlePointerLeave = () => {
        setIsHovered(false);

        setIsPressed(false);
    };

    const onAccessibilityAction = (event: AccessibilityActionEvent) => {
        if(event.nativeEvent.actionName === CONTEXT_MENU_ACCESSIBILITY_ACTION) {
            handleLongPress();

            return;
        }

        childElement.props.onAccessibilityAction?.(event);
    };

    const isNativeViewChild = childElement.type === View;

    const webStateProps: ContextMenuTriggerWebPropsType = isWeb && isNativeViewChild ? {
        onFocus: interactionHandlers.onFocus,
        onBlur: interactionHandlers.onBlur,
        onPointerEnter: handlePointerEnter,
        onPointerLeave: handlePointerLeave,
        onPointerCancel: handlePressOut,
        onPointerDown: handlePressIn,
        onPointerUp: handlePressOut
    } : {};

    const webProps: ContextMenuTriggerWebPropsType = isWeb ? {
        ...webStateProps,
        onContextMenu,
        onKeyDown
    } : {};

    const focusRingStyle = getFocusRingStyle({
        isFocusVisible: isFocusVisible && isNativeViewChild,
        borders,
        colors
    });

    const cornerStyle = getCornerStyle({
        radius: radiuses.actions,
        shapeConfigs
    });

    const renderLayer = () => {
        return <StateLayer
            borderRadius={radiuses.actions}
            isHovered={isOpen || isHovered}
            customTheme={customTheme}
            overlayColor="default"
            isPressed={isPressed}
        />;
    };

    const renderPointerAnchor = () => {
        if(!pointer) {
            return null;
        }

        return <View
            style={[
                stylesheet.pointerAnchor,
                {
                    start: pointer.start,
                    top: pointer.top
                }
            ]}
            ref={pointerAnchorRef}
            collapsable={false}
        />;
    };

    const renderChild = () => {
        const webChildProps = isWeb && isNativeViewChild ? {
            dataSet: {
                ...childElement.props.dataSet,
                [INTERACTION_STATE_DATA_KEY]: getInteractionStateName({
                    isDisabled: !!isDisabled,
                    isFocusVisible,
                    isHovered,
                    isPressed
                })
            },
            style: [
                childElement.props.style,
                stylesheet.childReset
            ]
        } : {};

        return cloneElement(childElement, {
            ...webChildProps,
            onAccessibilityAction: isWeb ? childElement.props.onAccessibilityAction : onAccessibilityAction,
            accessibilityActions: isWeb ? childElement.props.accessibilityActions : [
                ...childElement.props.accessibilityActions ?? [],
                {
                    name: CONTEXT_MENU_ACCESSIBILITY_ACTION,
                    label: localize("more-actions")
                }
            ],
            "aria-controls": isOpen ? contentId : undefined,
            "aria-haspopup": "menu"
        });
    };

    if(isWeb) {
        return <View
            {...webProps}
            style={[
                stylesheet.anchor,
                cornerStyle,
                focusRingStyle,
                style
            ]}
            collapsable={false}
            ref={anchorRef}
        >
            {renderChild()}
            {renderLayer()}
            {renderPointerAnchor()}
        </View>;
    }

    return <Pressable
        delayLongPress={durations.longPress}
        onLongPress={handleLongPress}
        onPressOut={handlePressOut}
        onPressIn={handlePressIn}
        style={[
            stylesheet.anchor,
            style
        ]}
        disabled={isDisabled}
        accessible={false}
        ref={anchorRef}
    >
        {renderChild()}
        {renderLayer()}
    </Pressable>;
};
export default ContextMenuTrigger;
